import * as Api from '@ViewModels';
import { useMutation } from '@tanstack/react-query';
import { useUserSession } from '../../models/hooks/appStateHooks';
import { invalidateFieldMappingsQuery } from './useGetExternalIntegrationFieldMappings';

export const useReplaceExternalIntegrationFieldMappingMutation = ({
	onError,
	impersonationContext,
}: {
	onError?: (error: Api.IOperationResultNoValue) => void;
	impersonationContext?: Api.IImpersonationContext;
} = {}) => {
	const userSession = useUserSession();

	const fieldMappingsUrl = (integrationProvider: Api.IntegrationProvider, path = '') =>
		Api.ImpersonationBroker.composeApiUrl({
			impersonationContext,
			urlPath: `integrations/${integrationProvider}/fieldMappings${path}`,
		});

	return useMutation({
		mutationFn: async ({
			integrationProvider,
			previous,
			next,
		}: {
			integrationProvider: Api.IntegrationProvider;
			previous?: Api.IIntegrationFieldMapping;
			next: Api.IIntegrationFieldMapping;
		}) => {
			// The API rejects a POST over an existing map to the same Levitate field, so the old one has to go first
			if (previous) {
				await userSession.webServiceHelper.callAsync(
					fieldMappingsUrl(integrationProvider, `/${previous.externalObjectType}/${previous.externalFieldName}`),
					'DELETE'
				);
			}
			try {
				await userSession.webServiceHelper.callAsync(fieldMappingsUrl(integrationProvider), 'POST', [next]);
			} catch (error) {
				// Put the old map back so a failed save doesn't silently fall back to the provider default
				if (previous) {
					await userSession.webServiceHelper
						.callAsync(fieldMappingsUrl(integrationProvider), 'POST', [previous])
						.catch((): void => undefined);
				}
				throw error;
			}
		},
		onError,
		onSettled: (_data, _error, { integrationProvider, next }) =>
			invalidateFieldMappingsQuery({
				integrationProvider,
				objectType: next.externalObjectType,
				impersonationContext,
			}),
	});
};
